import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { InfoIcon, WarningIcon, SettingsIcon } from '@parchment/icons';
import { apple, emperor, hotCinnamon, thunderBird, white, mercury, curiousBlue } from '../Colors/Colors';
import Button from '../Button/Button';
import Anchor from '../Typography/Anchor';
import { AddIcon, OpenNewIcon } from '@parchment/icons';
const buttonContexts = ['default', 'configure', 'dark', 'error', 'primary', 'warning'];

const noop = () => {
  /* Do nothing */
};

const getTitle = (title, children) => {
  if (!title) {
    return '';
  }

  return children ? "".concat(title, ": ") : title;
};

const getFontsize = size => {
  switch (size) {
    case 'small':
      {
        return '12px;';
      }

    case 'large':
      {
        return '16px;';
      }

    case 'medium':
    default:
      {
        return '14px;';
      }
  }
};

const handleDismiss = function (callBack) {
  callBack();
};

const handleAction = function (callBack) {
  callBack();
};

export const Banner = _ref => {
  let {
    message,
    context = 'dark',
    dismissable = false,
    onAction = noop,
    visible = true,
    isTransparent = false,
    size = 'medium',
    withAlert = true,
    hideIcon = false,
    actionType = 'button',
    icon,
    action,
    title,
    actionLink,
    actionProps,
    className,
    onDismiss
  } = _ref;

  if (!visible) {
    return null;
  }

  const theme = useTheme();
  const buttonContext = buttonContexts.includes(context) ? getThemeAttributes(context, theme, 'buttonContext', isTransparent) : undefined;
  const ghostButton = getThemeAttributes(context, theme, 'ghostButton', isTransparent);

  const renderIcon = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IconGridItem, {
      "data-testid": "banner-icon"
    }, /*#__PURE__*/React.createElement(IconStyle, null, icon || getThemeAttributes(context, theme, 'icon', isTransparent))), /*#__PURE__*/React.createElement(VerticalHeaderGridItem, {
      context: context,
      isTransparent: isTransparent
    }));
  };

  const renderActionLink = () => {
    return /*#__PURE__*/React.createElement(ActionAnchorWrapper, null, /*#__PURE__*/React.createElement(Anchor, {
      "data-testid": "banner-action",
      href: actionLink,
      target: "_blank",
      isTransparent: isTransparent
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(OpenNewIcon, null), action ? action : actionLink)));
  };

  const renderActionButton = () => {
    return /*#__PURE__*/React.createElement(ActionButtonGridItem, {
      size: size
    }, /*#__PURE__*/React.createElement(Button, _extends({
      context: buttonContext,
      ghost: ghostButton,
      size: size === 'large' ? 'medium' : 'small',
      name: action ? action : 'banner-action-button',
      "data-testid": "banner-action",
      onClick: () => handleAction(onAction)
    }, actionProps), action));
  };

  return /*#__PURE__*/React.createElement(BannerStyle, {
    "data-testid": "banner",
    context: context,
    hideIcon: hideIcon,
    size: size,
    isTransparent: isTransparent,
    className: className,
    role: withAlert ? 'alert' : undefined
  }, !hideIcon && renderIcon(), /*#__PURE__*/React.createElement(MessageGridItem, null, /*#__PURE__*/React.createElement(MessageStyle, {
    context: context,
    isTransparent: isTransparent,
    title: title
  }, message, actionLink && actionType === 'link' && renderActionLink())), action && actionType === 'button' && renderActionButton(), dismissable && /*#__PURE__*/React.createElement(DismissGridItem, {
    size: size
  }, /*#__PURE__*/React.createElement(DismissIcon, {
    "data-testid": "icon-dismiss",
    context: context,
    isTransparent: isTransparent,
    onClick: () => onDismiss && handleDismiss(onDismiss)
  }, /*#__PURE__*/React.createElement(AddIcon, {
    rotate: "45deg"
  }))));
};

const getThemeAttributes = (context, theme, key, isTransparent) => {
  var _theme$whiteSpace;

  if (isTransparent && key === 'backgroundColor') return (theme === null || theme === void 0 ? void 0 : (_theme$whiteSpace = theme.whiteSpace) === null || _theme$whiteSpace === void 0 ? void 0 : _theme$whiteSpace.base) || white;
  const BannerContext = {
    dark: {
      backgroundColor: emperor,
      textColor: isTransparent ? emperor : white,
      linkColor: isTransparent ? emperor : white,
      buttonContext: 'dark',
      ghostButton: true,
      icon: /*#__PURE__*/React.createElement(InfoIcon, {
        "data-testid": "info-icon"
      })
    },
    light: {
      backgroundColor: mercury,
      textColor: isTransparent ? mercury : emperor,
      linkColor: isTransparent ? mercury : curiousBlue,
      buttonContext: 'dark',
      ghostButton: false,
      icon: /*#__PURE__*/React.createElement(InfoIcon, {
        "data-testid": "info-icon"
      })
    },
    success: {
      backgroundColor: apple,
      textColor: white,
      linkColor: isTransparent ? apple : white,
      buttonContext: 'primary',
      ghostButton: true,
      icon: /*#__PURE__*/React.createElement(InfoIcon, {
        "data-testid": "info-icon"
      })
    },
    warning: {
      backgroundColor: hotCinnamon,
      textColor: isTransparent ? hotCinnamon : white,
      linkColor: isTransparent ? hotCinnamon : white,
      buttonContext: 'warning',
      ghostButton: true,
      icon: /*#__PURE__*/React.createElement(WarningIcon, {
        "data-testid": "warning-icon"
      })
    },
    error: {
      backgroundColor: thunderBird,
      textColor: isTransparent ? thunderBird : white,
      linkColor: isTransparent ? thunderBird : white,
      buttonContext: 'error',
      ghostButton: true,
      icon: /*#__PURE__*/React.createElement(WarningIcon, {
        "data-testid": "warning-icon"
      })
    },
    configure: {
      backgroundColor: curiousBlue,
      textColor: isTransparent ? curiousBlue : white,
      linkColor: isTransparent ? curiousBlue : white,
      buttonContext: 'configure',
      ghostButton: true,
      icon: /*#__PURE__*/React.createElement(SettingsIcon, {
        "data-testid": "settings-icon"
      })
    },
    ghost: {
      backgroundColor: white,
      textColor: isTransparent ? emperor : white,
      linkColor: isTransparent ? curiousBlue : curiousBlue,
      buttonContext: 'default',
      ghostButton: false,
      icon: /*#__PURE__*/React.createElement(InfoIcon, {
        "data-testid": "info-icon"
      })
    }
  };
  return BannerContext[context][key];
};

const BannerStyle = /*#__PURE__*/styled.div.withConfig({
  displayName: "Banner__BannerStyle",
  componentId: "sc-142jc7w-0"
})(["display:grid;grid-template-columns:", ";background-color:", ";color:", ";font-size:", ";padding:5px;min-height:50px;"], _ref2 => {
  let {
    hideIcon,
    size
  } = _ref2;

  if (hideIcon) {
    return '1fr auto 30px;';
  } else {
    return size === 'small' ? '30px 8px 1fr 30px;' : '30px 8px 1fr auto 30px;';
  }
}, _ref3 => {
  let {
    context,
    theme,
    isTransparent
  } = _ref3;
  return isTransparent ? 'transparent' : getThemeAttributes(context, theme, 'backgroundColor', isTransparent);
}, _ref4 => {
  let {
    context,
    theme,
    isTransparent
  } = _ref4;
  return getThemeAttributes(context, theme, 'textColor', isTransparent);
}, _ref5 => {
  let {
    size
  } = _ref5;
  return getFontsize(size);
});
/* GridItems*/

const IconGridItem = /*#__PURE__*/styled.div.withConfig({
  displayName: "Banner__IconGridItem",
  componentId: "sc-142jc7w-1"
})(["display:flex;flex-direction:column;justify-content:center;order:1;"]);
/* GridItems*/

const VerticalHeaderGridItem = /*#__PURE__*/styled.div.withConfig({
  displayName: "Banner__VerticalHeaderGridItem",
  componentId: "sc-142jc7w-2"
})(["height:100%;width:1px;margin-left:4px;background-color:", ";order:2;"], _ref6 => {
  let {
    context,
    theme,
    isTransparent
  } = _ref6;
  return isTransparent ? 'transparent' : getThemeAttributes(context, theme, 'textColor', isTransparent);
});
const MessageGridItem = /*#__PURE__*/styled.div.withConfig({
  displayName: "Banner__MessageGridItem",
  componentId: "sc-142jc7w-3"
})(["display:flex;flex-direction:column;align-items:center;align-self:center;margin:0 10px 0 15px;order:3;"]);
const ActionButtonGridItem = /*#__PURE__*/styled.div.withConfig({
  displayName: "Banner__ActionButtonGridItem",
  componentId: "sc-142jc7w-4"
})(["display:flex;flex-direction:column;justify-content:center;padding:", ";order:", ";grid-column:", ";@media (max-width:675px){grid-column:1 / 5;order:5;padding:10px;}a{display:flex;}"], _ref7 => {
  let {
    size
  } = _ref7;
  return size === 'small' ? '10px 0px' : 'none';
}, _ref8 => {
  let {
    size
  } = _ref8;
  return size === 'small' ? '5' : '4';
}, _ref9 => {
  let {
    size
  } = _ref9;
  return size === 'small' ? '1 / 5' : 'auto';
});
const ActionAnchorWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "Banner__ActionAnchorWrapper",
  componentId: "sc-142jc7w-5"
})(["display:flex;flex-direction:column;justify-content:center;width:max-content;svg{padding-right:2px;}a{&:hover{color:white;}}"]);
const DismissGridItem = /*#__PURE__*/styled.div.withConfig({
  displayName: "Banner__DismissGridItem",
  componentId: "sc-142jc7w-6"
})(["display:flex;flex-direction:column;justify-content:center;order:", ";@media (max-width:675px){order:4;}"], _ref10 => {
  let {
    size
  } = _ref10;
  return size === 'small' ? '4' : '5';
});
/*Style*/

const IconStyle = /*#__PURE__*/styled.div.withConfig({
  displayName: "Banner__IconStyle",
  componentId: "sc-142jc7w-7"
})(["font-size:18px;padding:5px 11px 5px 5px;"]);
const MessageStyle = /*#__PURE__*/styled.div.withConfig({
  displayName: "Banner__MessageStyle",
  componentId: "sc-142jc7w-8"
})(["width:100%;margin:10px 0;a{text-decoration:underline;font-weight:700;color:", ";}:before{content:'", "';font-weight:700;text-transform:uppercase;}"], _ref11 => {
  let {
    context,
    theme,
    isTransparent
  } = _ref11;
  return getThemeAttributes(context, theme, 'linkColor', isTransparent);
}, _ref12 => {
  let {
    title,
    children
  } = _ref12;
  return getTitle(title, children);
});
const DismissIcon = /*#__PURE__*/styled.div.withConfig({
  displayName: "Banner__DismissIcon",
  componentId: "sc-142jc7w-9"
})(["border:0 none;background-color:transparent;color:", ";font-size:18px;padding:5px 11px 5px 5px;:focus,:hover{opacity:0.8}"], _ref13 => {
  let {
    context,
    theme,
    isTransparent
  } = _ref13;
  return getThemeAttributes(context, theme, 'textColor', isTransparent);
});